import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Star, MapPin, Quote, Car, ShowerHead, Users, HeartHandshake } from "lucide-react";
import Seo, { breadcrumbSchema, faqSchema, speakableSchema } from "../components/Seo";
import { Reveal, Eyebrow, BtnPrimary, BtnGhost, CtaBand, FaqList, GoogleReviewCta } from "../components/Shared";
import { useGoogleReviews } from "../components/GoogleReviews";
import { HeroVideo } from "../components/HeroVideo";
import { LeadBand } from "../components/LeadForm";
import { TownQuotes } from "../components/TownQuotes";
import { InstaStrip } from "../components/InstaStrip";
import { SITE, IMG, PROOF, TESTIMONIALS, CLASSES, GOLD_STANDARD } from "../data/mock";

const featured = ["crossfit", "olympic-weightlifting", "powerlifting", "big-engine", "fundamentals", "open-gym"];

const homeFaqs = [
  { q: "How much does CrossFit cost in West Roxbury?", a: "Memberships at Boston Iron Grit start at $225/month for three classes per week and $275/month for the Platinum Membership with unlimited classes, which includes the Live B.I.G. 365 wellness program. Punch cards, personal training and $35 drop-ins are also available — all month-to-month with no contracts." },
  { q: "Do I need experience to start CrossFit at Boston Iron Grit?", a: "No. Every new member starts with a free goal review and a beginner fundamentals program, and every workout in class scales to your current fitness level." },
  { q: "What time do classes start?", a: "The first CrossFit class starts at 5:15 AM Monday through Friday, with classes through the day up to the 6:30 PM class Monday through Thursday, plus weekend classes on Saturday and Sunday mornings." },
  { q: "Is there parking at the gym?", a: "Yes — we have free on-site parking, with the entrance on Sambuca Way Alley off VFW Parkway." },
  { q: "Do you have showers?", a: "Yes. The gym has showers, so you can train before work and head straight to the office." },
  { q: "Where exactly are you located?", a: "Boston Iron Grit is at 1230 VFW Parkway, West Roxbury, MA 02132 — minutes from Dedham, Roslindale, Brookline, Needham, Newton, Jamaica Plain and Hyde Park." },
  { q: "Can I try a class before joining?", a: "Yes. Start with a free 20-minute goal review — a conversation with a coach, with an optional free InBody scan and zero obligation to join." },
  { q: "Do you offer drop-ins for visiting athletes?", a: "Yes — experienced CrossFit athletes can drop in for $35 per class. Book ahead through the drop-in page so we can save you a spot." },
];

export default function Home() {
  const [tIndex, setTIndex] = useState(0);
  const gReviews = useGoogleReviews();
  useEffect(() => {
    const id = setInterval(() => setTIndex((i) => (i + 1) % TESTIMONIALS.length), 6000);
    return () => clearInterval(id);
  }, []);

  const t = TESTIMONIALS[tIndex];

  return (
    <>
      <Seo
        title="CrossFit West Roxbury, MA | Boston Iron Grit"
        description="Boston Iron Grit is a coached CrossFit and strength gym in West Roxbury. Beginner-friendly classes, HYROX, weightlifting and open gym. Free goal review."
        path="/"
        schemas={[breadcrumbSchema([{ name: "Home", path: "/" }]), faqSchema(homeFaqs), speakableSchema("/")]}
      />

      {/* ---------------- HERO ---------------- */}
      <section className="relative min-h-[92vh] flex items-center bg-[#0A0A0B] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-[position:62%_35%] sm:bg-center scale-105" style={{ backgroundImage: `url(${IMG.hero})` }} aria-hidden="true" />
        <HeroVideo base="/video/hero-loop-12s" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] via-[#0A0A0B]/80 to-[#0A0A0B]/30" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-[#0A0A0B]/40" aria-hidden="true" />

        <div className="relative max-w-7xl mx-auto px-6 py-24 w-full">
          <Reveal>
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-neutral-400">West Roxbury · Boston</span>
              <span className="w-8 h-px bg-[#9D0423]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-neutral-400">Est. 2011</span>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="font-heading font-extrabold uppercase text-5xl sm:text-6xl lg:text-[80px] text-white leading-[0.92] tracking-tight max-w-4xl">
              Boston's Best Strength &<br />
              Conditioning Gym<br />
              <span className="text-[#9D0423]">in West Roxbury.</span>
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-6 font-mono text-[12px] uppercase tracking-[0.18em] text-neutral-400 max-w-2xl leading-relaxed">
              Easy access to Downtown, Dedham, Needham, Brookline, Newton, and Boston's vibrant and diverse neighborhoods.
            </p>
            <p className="mt-5 text-neutral-300 text-base lg:text-lg leading-relaxed max-w-xl">
              Start safely with a one-on-one goal review and fundamentals program. Supportive coaches. Excellent
              programs for all levels. No experience required — every workout scales to you.
            </p>
          </Reveal>
          <Reveal delay={300}>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <BtnPrimary to="/get-started">Book a Free Goal Review</BtnPrimary>
              <BtnGhost to="/pricing">View Schedule & Pricing</BtnGhost>
            </div>
          </Reveal>
          <Reveal delay={400}>
            <div className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-3 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-400">
              <span className="flex items-center gap-1.5"><Star size={12} className="text-[#9D0423] fill-[#9D0423]" /> 5.0 Google rating</span>
              <span className="text-neutral-700">/</span>
              <span>15 years affiliated</span>
              <span className="text-neutral-700">/</span>
              <span>Beginner-friendly</span>
              <span className="text-neutral-700">/</span>
              <span>Parking & showers</span>
            </div>
          </Reveal>
        </div>

        <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-[#0A0A0B]/70 backdrop-blur-sm hidden md:block">
          <div className="max-w-7xl mx-auto px-6 grid grid-cols-4 divide-x divide-white/10">
            {PROOF.map((p) => (
              <div key={p.label} className="py-5 px-6 first:pl-0">
                <div className="font-heading font-extrabold text-2xl text-white">{p.value}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-1">{p.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- LIVE B.I.G. 365 FEATURE ---------------- */}
      <section className="relative bg-[#0D0D0F] border-y border-[#9D0423]/25 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05] bg-[radial-gradient(circle_at_15%_50%,#9D0423_1px,transparent_1px)] [background-size:22px_22px]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-20 grid lg:grid-cols-[1fr_auto] gap-10 items-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-[#E56A79] bg-[#9D0423]/10 border border-[#9D0423]/30 px-3 py-1.5 mb-5">
              New · Wellness & Longevity Program
            </div>
            <h2 className="font-heading font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.92] tracking-tight">
              Live B.I.G. <span className="text-[#9D0423]">365.</span>
            </h2>
            <p className="mt-5 text-neutral-300 text-base lg:text-lg leading-relaxed max-w-2xl">
              Five years in the making — our longevity program pairs with your training. Get a baseline with our
              B.I.G. Labs, and a comprehensive review by our medical partners helps you understand your health on a
              whole new level. Ready for the next step? Get your supplements, peptides and GLP-1s through our licensed
              providers with the members-only discount.
            </p>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {[
                { label: "B.I.G. Labs" },
                { label: "Peptides & GLP-1s", to: "/peptides-glp1-boston" },
                { label: "Supplements" },
                { label: "8 Daily Habits" },
                { label: "Body Composition" },
                { label: "App Coming Soon" },
              ].map((c) =>
                c.to ? (
                  <Link
                    key={c.label}
                    to={c.to}
                    data-testid="home-peptides-link"
                    className="font-mono text-[11px] uppercase tracking-[0.14em] text-[#E56A79] border border-[#9D0423]/40 hover:border-[#9D0423] hover:text-white px-3.5 py-2 bg-[#121214] transition-colors"
                  >
                    {c.label} →
                  </Link>
                ) : (
                  <span key={c.label} className="font-mono text-[11px] uppercase tracking-[0.14em] text-neutral-300 border border-white/12 px-3.5 py-2 bg-[#121214]">
                    {c.label}
                  </span>
                )
              )}
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="flex flex-col gap-4 shrink-0">
              <BtnPrimary to="/live-big-365">Explore the Program</BtnPrimary>
              <BtnGhost to="/get-started">Free Goal Review</BtnGhost>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- ANSWER BOX / INTRO ---------------- */}
      <section className="bg-[#0A0A0B] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Eyebrow>The Gym</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight">
              Fifteen years of<br />building strong,<br /><span className="text-neutral-600">happy humans.</span>
            </h2>
            <p className="mt-6 text-neutral-300 text-base leading-relaxed">
              We are one of the original CrossFit Boston gyms — and we are proud of our roots. The training
              methodology that started all the ruckus works, because our knowledgeable staff knows how to coach
              everyone from beginners to competitors. Our standards have stood the test of time.
            </p>
            <p className="mt-4 text-neutral-400 text-base leading-relaxed">
              From CrossFit to the Westie Barbell Club to our newest adventure, HYROX — we know how to train every
              level of athlete.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {[
                { icon: Users, text: "Supportive, no-judgement community" },
                { icon: HeartHandshake, text: "Coaches who scale every workout" },
                { icon: Car, text: "Easy on-site parking" },
                { icon: ShowerHead, text: "Clean facility with showers" },
              ].map((f) => (
                <div key={f.text} className="flex items-start gap-3 p-4 bg-[#121214] border border-white/5">
                  <f.icon size={17} className="text-[#9D0423] mt-0.5 shrink-0" />
                  <span className="text-[13px] text-neutral-300 leading-snug">{f.text}</span>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/about" className="inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.16em] text-white hover:text-[#9D0423] transition-colors">
                Our story since 2011 <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="relative">
              <img src={IMG.happy} alt="Smiling Boston Iron Grit member carrying dumbbells during a partner workout" className="w-full h-[340px] sm:h-[480px] object-cover object-[center_25%]" loading="lazy" />
              <div className="absolute -bottom-6 -left-6 bg-[#9D0423] px-7 py-5 hidden sm:block">
                <div className="font-heading font-extrabold text-3xl text-white leading-none">EST. 2011</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/80 mt-1.5">Affiliate #1360</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- PROGRAMS ---------------- */}
      <section className="bg-[#0D0D0F] py-20 lg:py-28 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <Reveal>
              <Eyebrow>Programs</Eyebrow>
              <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight">
                Train for your goal.<br /><span className="text-neutral-600">Scaled to your level.</span>
              </h2>
            </Reveal>
            <Reveal delay={100}>
              <Link to="/classes" className="inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.16em] text-neutral-300 hover:text-white transition-colors border border-white/15 hover:border-white/40 px-6 py-3.5">
                All 10 Programs <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.map((slug, i) => {
              const c = CLASSES.find((x) => x.slug === slug);
              return (
                <Reveal key={slug} delay={i * 80}>
                  <Link to={`/classes/${c.slug}`} className="group relative block h-[340px] overflow-hidden bg-[#121214]">
                    <img
                      src={c.image}
                      alt={`${c.name} — coached training program`}
                      className="absolute inset-0 w-full h-full object-cover object-[center_25%] opacity-60 group-hover:opacity-75 group-hover:scale-105 transition-all duration-700"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-[#0A0A0B]/30 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-6">
                      <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#E56A79] mb-2">{c.heroLabel}</p>
                      <h3 className="font-heading font-extrabold uppercase text-2xl text-white tracking-tight">{c.name}</h3>
                      <p className="mt-2 text-[13px] text-neutral-400 leading-snug max-h-0 opacity-0 group-hover:max-h-20 group-hover:opacity-100 transition-all duration-500 overflow-hidden">
                        {c.short}
                      </p>
                      <span className="mt-3 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-white">
                        Explore <ArrowUpRight size={13} className="text-[#9D0423] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>

          {/* HYROX banner card */}
          <Reveal delay={100}>
            <Link to="/hyrox" className="group relative block mt-5 h-[220px] lg:h-[260px] overflow-hidden">
              <img src={IMG.hyroxSandbag} alt="HYROX training athlete carrying sandbag" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-75 group-hover:scale-[1.03] transition-all duration-700" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] via-[#0A0A0B]/50 to-transparent" />
              <div className="absolute inset-0 flex items-center px-8 lg:px-12">
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#E56A79] mb-2">New Program</p>
                  <h3 className="font-heading font-extrabold uppercase text-3xl lg:text-5xl text-white tracking-tight">
                    HYROX Training <span className="text-[#9D0423]">Boston</span>
                  </h3>
                  <p className="mt-2 text-neutral-300 text-sm lg:text-base max-w-lg">8 runs. 8 stations. Train for the world's fastest-growing fitness race — right here in West Roxbury.</p>
                  <span className="mt-4 inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.16em] text-white group-hover:text-[#E56A79] transition-colors">
                    Enter the race <ArrowRight size={14} />
                  </span>
                </div>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ---------------- GOLD STANDARD ---------------- */}
      <section className="relative bg-[#0A0A0B] overflow-hidden border-b border-white/5" data-testid="home-gold-standard-band">
        <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle_at_85%_50%,#D4AF37_1px,transparent_1px)] [background-size:22px_22px]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-20 grid lg:grid-cols-[1fr_auto] gap-10 items-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-[#E8C766] bg-[#D4AF37]/10 border border-[#D4AF37]/30 px-3 py-1.5 mb-5">
              {GOLD_STANDARD.tagline}
            </div>
            <h2 className="font-heading font-extrabold uppercase text-4xl sm:text-5xl text-white leading-[0.92] tracking-tight">
              Take your training to the next level.<br />
              <span className="text-[#D4AF37]">Start with our Gold Standard Programs.</span>
            </h2>
            <p className="mt-5 text-neutral-300 text-base lg:text-lg leading-relaxed max-w-2xl">
              {GOLD_STANDARD.blurb}
            </p>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {GOLD_STANDARD.tiers.map((t) => (
                <span key={t.name} className="font-mono text-[11px] uppercase tracking-[0.14em] text-neutral-300 border border-[#D4AF37]/25 px-3.5 py-2 bg-[#121214]">
                  {t.name} · <span className="text-[#E8C766]">{t.price}</span>
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="flex flex-col gap-4 shrink-0">
              <Link
                to="/gold-standard"
                data-testid="home-gold-standard-cta"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#c19d2c] text-[#0A0A0B] font-mono font-semibold text-[13px] uppercase tracking-[0.14em] px-7 py-4 transition-all duration-200 hover:translate-y-[-2px]"
              >
                Explore the Programs <ArrowRight size={15} />
              </Link>
              <BtnGhost to="/get-started">Free Goal Review</BtnGhost>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- COMMUNITY ---------------- */}
      <section className="bg-[#0A0A0B] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="grid grid-cols-2 gap-4">
              <img src={IMG.pullups} alt="Boston Iron Grit member performing kipping pull-ups" className="w-full h-64 object-cover object-[center_25%] mt-8" loading="lazy" />
              <img src={IMG.chalk} alt="Boston Iron Grit member pressing a dumbbell overhead in competition" className="w-full h-64 object-cover object-[center_25%]" loading="lazy" />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <Eyebrow>Community</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight">
              More than a gym.<br /><span className="text-neutral-600">It's your corner.</span>
            </h2>
            <p className="mt-6 text-neutral-300 text-base leading-relaxed">
              We affiliated in 2011 with a mission: build a community of fit, healthy, happy people who live their best
              life — and give back. Over 15 years, the Iron Grit team has volunteered for homeless children and
              Boston's most impoverished neighborhoods while keeping each other strong.
            </p>
            <p className="mt-4 text-neutral-400 text-base leading-relaxed">
              All shapes, sizes, ages and abilities. Truly no judgements. Members call it their second home — some
              call it their church.
            </p>
            <div className="mt-8">
              <BtnGhost to="/about">Meet the Coaches</BtnGhost>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- TESTIMONIALS ---------------- */}
      <section className="bg-[#0D0D0F] py-20 lg:py-28 border-y border-white/5 relative overflow-hidden">
        <div className="absolute top-10 left-10 opacity-[0.04]" aria-hidden="true">
          <Quote size={280} className="text-white" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-left">
          <Reveal>
            <Eyebrow>{gReviews ? `${Number(gReviews.rating).toFixed(1)} on Google · ${gReviews.count} reviews` : "5.0 on Google"}</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight mb-10">
              What members say
            </h2>
          </Reveal>
          <div key={tIndex} className="animate-[fadeIn_0.6s_ease]">
            <div className="flex gap-1 mb-5">
              {[...Array(5)].map((_, i) => (
                <Star key={`star-${i + 1}`} size={16} className="text-[#9D0423] fill-[#9D0423]" />
              ))}
            </div>
            <blockquote data-testid="testimonial-quote" className="text-xl lg:text-2xl text-neutral-200 leading-relaxed font-light">“{t.quote}”</blockquote>
            <div className="mt-6 flex items-center gap-3">
              <div className="w-10 h-10 bg-[#9D0423]/15 border border-[#9D0423]/30 flex items-center justify-center font-heading font-bold text-[#E56A79]">
                {t.name.charAt(0)}
              </div>
              <div>
                <div className="text-white text-sm font-semibold">{t.name}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{t.tag}</div>
              </div>
            </div>
          </div>
          <div className="mt-10 flex items-center gap-2">
            {TESTIMONIALS.map((tm, i) => (
              <button
                key={tm.name}
                data-testid={`testimonial-dot-${i}`}
                onClick={() => setTIndex(i)}
                aria-label={`Testimonial ${i + 1}`}
                className={`h-1 transition-all duration-300 ${i === tIndex ? "w-10 bg-[#9D0423]" : "w-5 bg-white/15 hover:bg-white/30"}`}
              />
            ))}
          </div>
          <GoogleReviewCta className="mt-10" />
        </div>
      </section>

      {/* ---------------- LOCATION ---------------- */}
      <section className="bg-[#0A0A0B] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Eyebrow>Find Us</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight">
              West Roxbury.<br /><span className="text-neutral-600">Easy in, easy out.</span>
            </h2>
            <p className="mt-6 text-neutral-300 text-base leading-relaxed max-w-lg">
              Right on VFW Parkway with free on-site parking — minutes from Dedham, Roslindale, Brookline, Needham,
              Newton, Jamaica Plain and Hyde Park. No downtown traffic, no parking meters.
            </p>
            <div className="mt-8 space-y-3 font-mono text-[13px] text-neutral-300">
              <div className="flex items-center gap-3"><MapPin size={15} className="text-[#9D0423]" /> {SITE.address.full}</div>
              <div className="pl-7 text-neutral-500 text-[12px]">Entrance on Sambuca Way Alley</div>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <BtnPrimary href={SITE.mapsUrl} >Get Directions</BtnPrimary>
              <BtnGhost to="/contact">Contact Us</BtnGhost>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="relative h-[420px] border border-white/10 overflow-hidden">
              <iframe
                title="Boston Iron Grit map — 1230 VFW Parkway, West Roxbury MA"
                src="https://www.google.com/maps?q=1230+VFW+Parkway,+West+Roxbury,+MA+02132&output=embed"
                className="w-full h-full"
                style={{ border: 0, filter: "invert(90%) hue-rotate(180deg) grayscale(30%)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- SERVICE AREA ---------------- */}
      <section className="bg-[#0D0D0F] py-14 lg:py-16 border-y border-white/5" data-testid="home-service-area">
        <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
          <div className="max-w-xl">
            <Eyebrow>Beyond West Roxbury</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white leading-[0.95] tracking-tight">
              Serving the towns<br /><span className="text-neutral-600">next door, too.</span>
            </h2>
            <p className="mt-4 text-neutral-400 text-[15px] leading-relaxed">
              Half our members drive in from the neighborhoods around us — the Parkway location makes it an easy, traffic-light trip from every direction.
            </p>
          </div>
          <div className="max-w-md">
            <div className="flex flex-wrap gap-2">
              {SITE.serviceAreas.filter((a) => a !== "West Roxbury").map((a) => (
                <Link
                  key={a}
                  to="/west-roxbury-gym"
                  className="font-mono text-[12px] uppercase tracking-[0.12em] text-neutral-300 border border-white/15 hover:border-[#9D0423] hover:text-white px-4 py-2 transition-colors"
                  data-testid={`home-area-${a.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {a}
                </Link>
              ))}
            </div>
            <Link
              to="/west-roxbury-gym"
              className="mt-5 inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.14em] text-[#9D0423] hover:text-[#ff4a56] transition-colors"
              data-testid="home-service-area-link"
            >
              Drive times & directions <ArrowRight size={14} />
            </Link>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 mt-12">
          <TownQuotes />
        </div>
      </section>

      <InstaStrip />

      {/* ---------------- FAQ ---------------- */}
      <section className="bg-[#0A0A0B] py-16 lg:py-24" data-testid="home-faq">
        <div className="max-w-4xl mx-auto px-6">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#9D0423] mb-3">// Straight Answers</p>
          <p className="text-[14px] text-neutral-400 leading-relaxed mb-8" data-speakable="true">
            Boston Iron Grit · 1230 VFW Parkway, West Roxbury, MA 02132 · 617-637-8900 · Open Mon–Fri 5:15 AM–7:30 PM, Sat 6:30 AM–12 PM, Sun 8 AM–12 PM · Free parking on Sambuca Way Alley.
          </p>
          <FaqList faqs={homeFaqs} title="Frequently Asked Questions" />
        </div>
      </section>

      <LeadBand
        formName="homepage"
        programInterest="Free Goal Review"
        title="Tell us where you're starting."
        sub="Fill this out and a coach will reach out — usually the same day — to help you pick the right first step. No pressure, no sales script."
      />

      <CtaBand />
    </>
  );
}
